import {
  Box,
  Card,
  CardContent,
  Collapse,
  Divider,
  Grid,
  IconButton
} from '@mui/material'
import Typography from '@mui/material/Typography'
import React, { useEffect, useState } from 'react'
import {
  type CommitteeAddress,
  type CommitteeBasicInformation,
  type CommitteePhone,
  updateComAddress,
  type UsAddress
} from 'services'
import {
  AddressSlotEditor,
  EditActions,
  usAddressIsValid,
  useGlobalMessageHandler,
  PHONE_TYPE_ID, FAX_TYPE_ID, useBuildingLuts, useUsStateLuts
} from 'components'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { formatErrorFieldMessages } from 'interfaces'
import { filterBuildingsForCommittee, getEmptyCommitteePhone } from 'helpers'
import { PhoneFaxSection } from 'components/us/common/phone-fax-section'

export interface CommitteeAddressBlockProps {
  comAddress: CommitteeAddress
  originalBasicInfo: CommitteeBasicInformation
  resetAddress: () => void
  updateAddress: (address: CommitteeAddress) => void
  startExpanded?: boolean
  startDisabled?: boolean
}

export const CommitteeAddressBlock: React.FC<CommitteeAddressBlockProps> = ({
  comAddress,
  originalBasicInfo,
  resetAddress,
  updateAddress,
  startExpanded = false,
  startDisabled = true
}) => {
  const [expanded, setExpanded] = useState(startExpanded)
  const [disabled, setDisabled] = useState(startDisabled)
  const [loadingClick, setLoadingClick] = useState(false)
  const { lutValues: buildingLutValues, loading: buildingLoading } = useBuildingLuts()
  const { lutValues: stateLut, loading: stateLoading } = useUsStateLuts()
  const filteredBuildings = filterBuildingsForCommittee(buildingLutValues.buildings, originalBasicInfo)

  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()
  const secondaryAddress = comAddress?.secondary
  const primaryAddress = comAddress?.primary
  const phone = comAddress?.phones?.find(phone => phone.typeId === PHONE_TYPE_ID && phone.displayOrder === 1)
  const fax = comAddress?.phones?.find(phone => phone.typeId === FAX_TYPE_ID && phone.displayOrder === 1)

  useEffect(() => {
    if (stateLoading) return
    if (secondaryAddress?.state?.id != null) {
      secondaryAddress.state.desc = stateLut.states.get(secondaryAddress.state.id)?.desc
    }
    if (primaryAddress?.state?.id != null) {
      primaryAddress.state.desc = stateLut.states.get(primaryAddress.state.id)?.desc
    }
  }, [secondaryAddress?.state?.id, primaryAddress?.state?.id, stateLut.states, stateLoading])
  const handleExpandClick = (): void => {
    setExpanded(!expanded)
  }

  const changeSecondaryValue = (updatedAddress: UsAddress): void => {
    updateAddress({
      ...comAddress,
      secondary: updatedAddress
    })
  }

  const changePrimaryValue = (updatedAddress: UsAddress): void => {
    updateAddress({
      ...comAddress,
      primary: updatedAddress
    })
  }

  const handleSwitchClick = (value: 1 | 2): void => {
    updateAddress({ ...comAddress, mailingAddress: value })
  }

  const handleSave = (): void => {
    clearMessages()
    if (!usAddressIsValid(comAddress, showErrorMessage)) return
    setLoadingClick(true)
    updateComAddress(comAddress.committeeId, comAddress.id, comAddress).then((updatedInfo) => {
      if (updatedInfo.result != null && updatedInfo.succeeded) {
        setDisabled(true)
        showSuccessMessage('Address updated successfully')
      } else {
        showErrorMessage(formatErrorFieldMessages(updatedInfo.errors))
      }
    }).catch((error) => {
      showErrorMessage(formatErrorFieldMessages(error.response.data.errors))
    }).finally(() => { setLoadingClick(false) })
  }

  const handleReset = (): void => {
    clearMessages()
    resetAddress()
    setDisabled(true)
    setLoadingClick(false)
  }

  if (primaryAddress == null || secondaryAddress == null) {
    return <Typography>Address is not available for this committee</Typography>
  }

  const handlePhoneChange = (fax: boolean, field: keyof CommitteePhone, value: string): void => {
    const updatedPhones = comAddress.phones.map(phone => {
      if (phone.typeId === (!fax ? PHONE_TYPE_ID : FAX_TYPE_ID) && phone.displayOrder === 1) {
        return { ...phone, [field]: value }
      }
      return phone
    })
    updateAddress({ ...comAddress, phones: updatedPhones })
  }

  const handleAddPhone = (isPhoneType: boolean): void => {
    const newPhone = getEmptyCommitteePhone(comAddress.committeeId, comAddress.id, isPhoneType ? PHONE_TYPE_ID : FAX_TYPE_ID)
    const updatedPhones = [...comAddress.phones, newPhone]
    updateAddress({ ...comAddress, phones: updatedPhones })
  }

  const handleDeletePhone = (isPhoneType: boolean): void => {
    const updatedPhones = comAddress.phones.filter(phone => phone.typeId !== (isPhoneType ? PHONE_TYPE_ID : FAX_TYPE_ID))
    updateAddress({ ...comAddress, phones: updatedPhones })
  }

  return (<Card>
      <CardContent sx={{
        px: 1,
        pt: 0,
        '&:last-child': { pb: expanded ? 'default' : 1 }
      }}>
        <Grid container alignItems={'center'} marginTop={1} marginBottom={1}>
          {!expanded &&
            <IconButton onClick={handleExpandClick} aria-expanded={expanded} aria-label="show more">
              <ExpandMoreIcon/>
            </IconButton>
          }
          {expanded &&
            <IconButton onClick={handleExpandClick} aria-expanded={expanded} aria-label="show less">
              <ExpandLessIcon/>
            </IconButton>
          }
          <Typography variant="body1" color={'primary'} sx={{ fontWeight: 'bold' }}>
            {`${comAddress.displayOrder === 1 ? 'Majority' : 'Minority'}: `}
            {!expanded && `${comAddress.primary.building}, ${comAddress.primary.room} | ${comAddress.primary.addressOne} ${comAddress.primary.addressTwo ?? ''} - ${comAddress.primary.city}, ${
              comAddress.primary.state?.desc ?? 'N/A'
            }`}
          </Typography>
        </Grid>
        <Collapse in={expanded} timeout="auto">
            <Grid container justifyContent={'flex-start'}>
                  <Grid item xs={12}>
                    <PhoneFaxSection
                      phone={phone}
                      fax={fax}
                      disabled={disabled}
                      handlePhoneChange={handlePhoneChange}
                      handleAddPhone={handleAddPhone}
                      handleDeletePhone={handleDeletePhone}
                    />
                  </Grid>
              <Grid item xs={5.95}>
                <AddressSlotEditor
                  label="Primary"
                  slot={primaryAddress}
                  disabled={disabled}
                  onSlotChange={changePrimaryValue}
                  buildings={filteredBuildings}
                  buildingLoading={buildingLoading}
                  mailingChecked={comAddress.mailingAddress === 1}
                  onMailingToggle={() => { handleSwitchClick(1) }}
                />
            </Grid>
              <Grid item xs={0.1}>
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Divider orientation="vertical" flexItem />
                </Box>
              </Grid>
            <Grid item xs={5.95}>
              <AddressSlotEditor
                label="Secondary"
                slot={secondaryAddress}
                disabled={disabled}
                onSlotChange={changeSecondaryValue}
                buildings={filteredBuildings}
                buildingLoading={buildingLoading}
                mailingChecked={comAddress.mailingAddress === 2}
                onMailingToggle={() => { handleSwitchClick(2) }}
              />
            </Grid>
            <Grid item xs={12} display={'flex'} justifyContent={'flex-end'}>
              <EditActions
                entityName='Address'
                editing={!disabled}
                saving={loadingClick}
                savePlacement='left'
                onEdit={() => { setDisabled(false) }}
                onCancel={handleReset}
                onSave={handleSave}
              />
            </Grid>
            </Grid>
        </Collapse>
      </CardContent>
    </Card>

  )
}
